"use client";

import { useState } from "react";
import type { FilterOptions, SearchParams } from "@/app/lib/types";

interface Props {
  options: FilterOptions;
  filters: SearchParams;
  onChange: (filters: SearchParams) => void;
}

export function SearchFilters({ options, filters, onChange }: Props) {
  const [open, setOpen] = useState<Record<string, boolean>>({
    demographics: true,
    diagnostics: true,
    signalQuality: false,
    meta: false,
  });

  function toggle(section: string) {
    setOpen((prev) => ({ ...prev, [section]: !prev[section] }));
  }

  function update(key: string, value: unknown) {
    onChange({ ...filters, [key]: value, page: 1 });
  }

  function toggleArray(key: string, value: string) {
    const arr = (filters[key as keyof SearchParams] as string[] | undefined) || [];
    const next = arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];
    update(key, next.length ? next : undefined);
  }

  return (
    <div className="space-y-1">
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search reports..."
          className="w-full px-3 py-2 border rounded text-sm"
          value={filters.query || ""}
          onChange={(e) => update("query", e.target.value || undefined)}
        />
      </div>

      <details open={open.demographics} className="border rounded bg-gradient-to-r from-blue-50/40 to-transparent">
        <summary
          className="px-3 py-2 cursor-pointer font-medium text-sm text-blue-700 hover:bg-blue-50/60 rounded"
          onClick={(e) => { e.preventDefault(); toggle("demographics"); }}
        >
          Demographics
        </summary>
        <div className="px-3 py-2 space-y-2">
          <div className="flex gap-2 items-center">
            <label className="text-xs">Age</label>
            <input type="number" placeholder="Min" className="w-16 px-2 py-1 border rounded text-xs"
              value={filters.age_min ?? ""} onChange={(e) => update("age_min", e.target.value ? Number(e.target.value) : undefined)} />
            <span>-</span>
            <input type="number" placeholder="Max" className="w-16 px-2 py-1 border rounded text-xs"
              value={filters.age_max ?? ""} onChange={(e) => update("age_max", e.target.value ? Number(e.target.value) : undefined)} />
          </div>
          <div className="flex gap-2 items-center">
            <label className="text-xs">Sex</label>
            <div className="flex gap-1">
              {[0, 1].map((v) => (
                <button key={v} className={`px-2 py-1 text-xs rounded border ${filters.sex === v ? "bg-blue-500 text-white" : ""}`}
                  onClick={() => update("sex", filters.sex === v ? undefined : v)}>
                  {v === 0 ? "F" : "M"}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <label className="text-xs">Height</label>
            <input type="number" placeholder="Min" className="w-16 px-2 py-1 border rounded text-xs"
              value={filters.height_min ?? ""} onChange={(e) => update("height_min", e.target.value ? Number(e.target.value) : undefined)} />
            <span>-</span>
            <input type="number" placeholder="Max" className="w-16 px-2 py-1 border rounded text-xs"
              value={filters.height_max ?? ""} onChange={(e) => update("height_max", e.target.value ? Number(e.target.value) : undefined)} />
          </div>
          <div className="flex gap-2 items-center">
            <label className="text-xs">Weight</label>
            <input type="number" placeholder="Min" className="w-16 px-2 py-1 border rounded text-xs"
              value={filters.weight_min ?? ""} onChange={(e) => update("weight_min", e.target.value ? Number(e.target.value) : undefined)} />
            <span>-</span>
            <input type="number" placeholder="Max" className="w-16 px-2 py-1 border rounded text-xs"
              value={filters.weight_max ?? ""} onChange={(e) => update("weight_max", e.target.value ? Number(e.target.value) : undefined)} />
          </div>
        </div>
      </details>

      <details open={open.diagnostics} className="border rounded bg-gradient-to-r from-emerald-50/40 to-transparent">
        <summary
          className="px-3 py-2 cursor-pointer font-medium text-sm text-emerald-700 hover:bg-emerald-50/60 rounded"
          onClick={(e) => { e.preventDefault(); toggle("diagnostics"); }}
        >
          Diagnostics
        </summary>
        <div className="px-3 py-2 space-y-2">
          <div className="flex flex-wrap gap-1">
            {options.diagnostic_superclasses.map((sc) => (
              <button key={sc}
                className={`px-2 py-1 text-xs rounded border ${(filters.diagnostic_superclass as string[] | undefined)?.includes(sc) ? "bg-blue-500 text-white" : ""}`}
                onClick={() => toggleArray("diagnostic_superclass", sc)}>
                {sc}
              </button>
            ))}
          </div>
          <div>
            <label className="text-xs block mb-1">SCP Codes</label>
            <select multiple className="w-full border rounded text-xs h-24"
              value={(filters.scp_codes as string[] | undefined) || []}
              onChange={() => {}}
              onMouseDown={(e) => { if ((e.target as HTMLElement).closest("option")) e.preventDefault(); }}
              onClick={(e) => {
                const option = (e.target as HTMLElement).closest("option") as HTMLOptionElement | null;
                if (!option) return;
                toggleArray("scp_codes", option.value);
              }}>
              {options.scp_codes.map((s) => (
                <option key={s.code} value={s.code}>{s.code} - {s.description}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs block mb-1">Heart Axis</label>
            <select multiple className="w-full border rounded text-xs h-20"
              value={(filters.heart_axis as string[] | undefined) || []}
              onChange={() => {}}
              onMouseDown={(e) => { if ((e.target as HTMLElement).closest("option")) e.preventDefault(); }}
              onClick={(e) => {
                const option = (e.target as HTMLElement).closest("option") as HTMLOptionElement | null;
                if (!option) return;
                toggleArray("heart_axis", option.value);
              }}>
              {options.heart_axes.map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
          </div>
        </div>
      </details>

      <details open={open.signalQuality} className="border rounded bg-gradient-to-r from-orange-50/40 to-transparent">
        <summary
          className="px-3 py-2 cursor-pointer font-medium text-sm text-orange-700 hover:bg-orange-50/60 rounded"
          onClick={(e) => { e.preventDefault(); toggle("signalQuality"); }}
        >
          Signal Quality
        </summary>
        <div className="px-3 py-2 space-y-1">
          {[
            { key: "has_baseline_drift", label: "Baseline Drift" },
            { key: "has_static_noise", label: "Static Noise" },
            { key: "has_burst_noise", label: "Burst Noise" },
            { key: "has_electrodes_problems", label: "Electrode Problems" },
            { key: "has_extra_beats", label: "Extra Beats" },
            { key: "has_pacemaker", label: "Pacemaker" },
          ].map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox"
                checked={Boolean(filters[key as keyof SearchParams])}
                onChange={(e) => update(key, e.target.checked ? true : undefined)} />
              {label}
            </label>
          ))}
        </div>
      </details>

      <details open={open.meta} className="border rounded bg-gradient-to-r from-violet-50/40 to-transparent">
        <summary
          className="px-3 py-2 cursor-pointer font-medium text-sm text-violet-700 hover:bg-violet-50/60 rounded"
          onClick={(e) => { e.preventDefault(); toggle("meta"); }}
        >
          Meta
        </summary>
        <div className="px-3 py-2 space-y-2">
          <div>
            <label className="text-xs block mb-1">Source</label>
            <select className="w-full border rounded text-xs px-1 py-1"
              value={filters.source || ""}
              onChange={(e) => update("source", e.target.value || undefined)}>
              <option value="">All Sources</option>
              {options.sources.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs block mb-1">Device</label>
            <select multiple className="w-full border rounded text-xs h-20"
              value={(filters.device as string[] | undefined) || []}
              onChange={(e) => update("device", Array.from(e.target.selectedOptions).map((o) => o.value))}>
              {options.devices.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox"
              checked={Boolean(filters.validated_by_human)}
              onChange={(e) => update("validated_by_human", e.target.checked ? true : undefined)} />
            Validated by Human
          </label>
          <div className="flex gap-2 items-center">
            <label className="text-xs">Strat Fold</label>
            <select className="border rounded text-xs px-1 py-1"
              value={filters.strat_fold || ""}
              onChange={(e) => update("strat_fold", e.target.value || undefined)}>
              <option value="">All</option>
              {options.strat_folds.map((f) => <option key={f} value={String(f)}>{f}</option>)}
            </select>
          </div>
        </div>
      </details>

      <hr className="my-3 border-gray-200" />
      <a
        href="/digitise"
        className="block w-full text-center px-4 py-2 bg-gradient-to-r from-blue-600 to-emerald-600 text-white rounded text-sm font-medium hover:from-blue-700 hover:to-emerald-700 transition-all"
      >
        + Digitise New
      </a>
    </div>
  );
}